import { PLATFORM_NAME } from '../../lib/brand';
import { DogPortrait } from './Illustrations';
import {
  BellIcon,
  CalendarIcon,
  CardIcon,
  ChartIcon,
  CapIcon,
  ClockIcon,
  HomeIcon,
  Paw,
  PawOutline,
  ShieldIcon,
  SitterIcon,
} from './icons';

/**
 * The product, drawn rather than screenshotted.
 *
 * A real screenshot goes stale the first time a column moves, and at this size
 * it would be unreadable anyway. This is a scale model: the same navigation,
 * the same four figures, the same list. The numbers are illustrative and the
 * page says so underneath.
 *
 * It is marked aria-hidden and the hero copy carries the meaning, so a screen
 * reader is not read a fake dashboard.
 */
export function AppMockup() {
  return (
    <div className="relative" aria-hidden>
      <DashboardPanel />
      {/* Below sm the phone would cover half the dashboard; see the class list
          for the lg gap. */}
      <PhonePanel className="absolute -bottom-10 -right-3 hidden w-[160px] sm:block lg:hidden xl:-right-20 xl:block xl:w-[168px]" />
    </div>
  );
}

function DashboardPanel() {
  return (
    <div className="overflow-hidden rounded-[18px] border border-[color:var(--color-promo-line)] bg-white shadow-[0_28px_70px_-30px_rgba(23,20,42,0.35)]">
      <div className="grid grid-cols-[104px_1fr] sm:grid-cols-[136px_1fr]">
        {/* --- Sidebar ---------------------------------------------------- */}
        <div className="border-r border-[color:var(--color-promo-line)] bg-[color:var(--color-promo-mist)] px-2.5 py-3.5 sm:px-3">
          <div className="mb-4 flex items-center gap-1.5 px-1">
            <Paw className="h-4 w-4 text-[color:var(--color-promo)]" />
            <span className="font-display text-[0.78rem] font-extrabold tracking-[-0.02em]">
              {PLATFORM_NAME}
            </span>
          </div>
          <nav className="space-y-0.5">
            {NAV.map((item, index) => (
              <div
                key={item.label}
                className={`flex items-center gap-1.5 rounded-md px-1.5 py-[5px] text-[0.63rem] font-medium ${
                  index === 0
                    ? 'bg-white text-[color:var(--color-promo)] shadow-[0_1px_2px_rgba(23,20,42,0.08)]'
                    : 'text-[color:var(--color-promo-body)]'
                }`}
              >
                <item.icon className="h-3 w-3 shrink-0" />
                <span className="truncate">{item.label}</span>
              </div>
            ))}
          </nav>
        </div>

        {/* --- Panel ------------------------------------------------------ */}
        <div className="flex min-w-0 flex-col p-3 sm:p-4">
          <p className="mb-2.5 text-[0.72rem] font-bold">Dashboard</p>

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {STATS.map((stat) => (
              <div
                key={stat.label}
                className="min-w-0 rounded-lg border border-[color:var(--color-promo-line)] px-2 py-1.5"
              >
                <p className="text-[0.5rem] leading-tight text-[color:var(--color-promo-body)]">
                  {stat.label}
                </p>
                <p className="font-display text-[0.92rem] font-extrabold leading-tight">
                  {stat.value}
                </p>
                <p className="text-[0.48rem] font-medium text-[#2f8a5b]">{stat.delta}</p>
              </div>
            ))}
          </div>

          <div className="mt-2.5 grid flex-1 gap-2 lg:grid-cols-[1.55fr_0.9fr]">
            <div className="min-w-0 rounded-lg border border-[color:var(--color-promo-line)] p-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-[0.56rem] font-semibold">Upcoming appointments</p>
                <p className="shrink-0 text-[0.5rem] text-[color:var(--color-promo)]">
                  View calendar
                </p>
              </div>
              <div className="space-y-[3px]">
                {APPOINTMENTS.map((row) => (
                  <div
                    key={row.when + row.pet}
                    className="flex items-center gap-1.5 rounded-md bg-[color:var(--color-promo-mist)] px-1.5 py-[5px] text-[0.5rem]"
                  >
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ background: row.tint }}
                    />
                    <span className="w-[52px] shrink-0 font-medium">{row.when}</span>
                    <span className="min-w-0 flex-1 truncate text-[color:var(--color-promo-body)]">
                      {row.service}
                    </span>
                    <span className="hidden w-9 shrink-0 truncate font-medium sm:block">
                      {row.pet}
                    </span>
                    {/* The client name is the first column to go: below xl
                        the fixed columns leave the service nothing to occupy,
                        and the service is the one that says what this is. */}
                    <span className="hidden w-12 shrink-0 truncate text-[color:var(--color-promo-body)] xl:block">
                      {row.client}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden min-w-0 rounded-lg border border-[color:var(--color-promo-line)] p-2 lg:block">
              <p className="mb-1 text-[0.56rem] font-semibold">What&rsquo;s new</p>
              <div className="space-y-1.5">
                {FEED.map((item) => (
                  <div key={item.title} className="flex gap-1.5">
                    <span className="mt-[1px] flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] bg-[color:var(--color-promo-tint)] text-[color:var(--color-promo)]">
                      <item.icon className="h-2.5 w-2.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[0.5rem] font-medium">{item.title}</p>
                      <p className="truncate text-[0.46rem] text-[color:var(--color-promo-body)]">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The client's side of the same product: booking, on a phone. */
function PhonePanel({ className }: { className?: string }) {
  return (
    <div
      className={`rounded-[22px] border-[5px] border-[#17142a] bg-white shadow-[0_22px_50px_-18px_rgba(23,20,42,0.5)] ${className ?? ''}`}
    >
      <div className="flex justify-center pt-1.5">
        <span className="h-[3px] w-9 rounded-full bg-[#17142a]" />
      </div>
      <div className="px-2.5 pb-2.5 pt-2">
        <p className="mb-1.5 text-[0.55rem] font-semibold">&larr; Book a service</p>

        <div className="overflow-hidden rounded-[9px] bg-gradient-to-b from-[#fbeedd] to-[#f4dcc0]">
          <DogPortrait className="mx-auto h-[62px] w-auto" />
        </div>

        <p className="mt-1.5 text-[0.6rem] font-bold">Walk &amp; play</p>
        <p className="text-[0.46rem] text-[color:var(--color-promo-body)]">30 min</p>

        <p className="mt-1.5 text-[0.46rem] font-medium text-[color:var(--color-promo-body)]">
          Select date
        </p>
        <div className="mt-1 grid grid-cols-5 gap-[3px]">
          {DAYS.map((day, index) => (
            <div
              key={day.date}
              className={`rounded-[5px] py-[3px] text-center ${
                index === 1
                  ? 'bg-[color:var(--color-promo)] text-white'
                  : 'bg-[color:var(--color-promo-mist)]'
              }`}
            >
              <p className="text-[0.36rem] leading-tight opacity-70">{day.day}</p>
              <p className="text-[0.5rem] font-semibold leading-tight">{day.date}</p>
            </div>
          ))}
        </div>

        <p className="mt-1.5 text-[0.46rem] font-medium text-[color:var(--color-promo-body)]">
          Select time
        </p>
        <div className="mt-1 grid grid-cols-3 gap-[3px]">
          {TIMES.map((time, index) => (
            <div
              key={time}
              className={`rounded-[5px] py-[3px] text-center text-[0.42rem] font-medium ${
                index === 1
                  ? 'bg-[color:var(--color-promo)] text-white'
                  : 'bg-[color:var(--color-promo-mist)]'
              }`}
            >
              {time}
            </div>
          ))}
        </div>

        <div className="mt-2 rounded-[6px] bg-[color:var(--color-promo)] py-[5px] text-center text-[0.5rem] font-bold text-white">
          Continue
        </div>
      </div>
    </div>
  );
}

const NAV = [
  { label: 'Dashboard', icon: HomeIcon },
  { label: 'Calendar', icon: CalendarIcon },
  { label: 'Bookings', icon: ClockIcon },
  { label: 'Clients', icon: SitterIcon },
  { label: 'Pets', icon: PawOutline },
  { label: 'Services', icon: CapIcon },
  { label: 'Payments', icon: CardIcon },
  { label: 'Agreements', icon: ShieldIcon },
  { label: 'Reports', icon: ChartIcon },
  { label: 'Settings', icon: ShieldIcon },
];

const STATS = [
  { label: 'Total bookings', value: '128', delta: '+18% this week' },
  { label: 'Revenue', value: '$5,640', delta: '+24% this week' },
  { label: 'New clients', value: '32', delta: '+12% this week' },
  { label: 'Pets on file', value: '98', delta: '+8% this week' },
];

const APPOINTMENTS = [
  { when: 'Today 9:00', service: 'Dog walking, 30 min', pet: 'Buddy', client: 'Samantha P.', tint: '#f6c9a8' },
  { when: 'Today 11:00', service: 'Pet sitting, 1 visit', pet: 'Luna', client: 'Michael T.', tint: '#c9b6f7' },
  { when: 'Today 2:00', service: 'Grooming, full groom', pet: 'Daisy', client: 'Ashley R.', tint: '#a8d5c2' },
  { when: 'Tue 10:00', service: 'Pet sitting, 2 visits', pet: 'Charlie', client: 'Jason K.', tint: '#f3b9c4' },
  { when: 'Tue 4:00', service: 'Dog walking, 45 min', pet: 'Pepper', client: 'Nadia H.', tint: '#b6cdf4' },
  { when: 'Wed 8:30', service: 'Drop-in visit, 1 visit', pet: 'Mochi', client: 'Owen B.', tint: '#c9b6f7' },
];

const FEED = [
  { icon: BellIcon, title: 'New booking', detail: '2 this week' },
  { icon: ShieldIcon, title: 'Agreement signed', detail: 'The Whitfields' },
  { icon: CardIcon, title: 'Payment in', detail: '$120.00' },
];

const DAYS = [
  { day: 'Mon', date: '20' },
  { day: 'Tue', date: '21' },
  { day: 'Wed', date: '22' },
  { day: 'Thu', date: '23' },
  { day: 'Fri', date: '24' },
];

const TIMES = ['8:00', '9:00', '10:00'];
